import type {
  AnalyticsSeries,
  CategoryDatum,
  Histogram,
  QualityReport,
  QualityRow,
} from '#shared/types';
import { BarChart, DonutChart, DonutLegend, LineChart } from '#client/components/charts';
import { Card } from '#client/components/ui/Card';
import { type Column, DataTable } from '#client/components/ui/DataTable';
import { formatPercent, formatterFor } from './format';
import { formatOutsideRangeShare } from './report.logic';
import { toFlagPoints, toHistogramSeries, toSeries } from './to-series';

/** The cards of the quality section, each given only the slice of the report it shows. */

export function FlagsCard({ flags }: { flags: QualityReport['flags'] }) {
  const points = toFlagPoints(flags);

  return (
    <Card
      className="c-an__cell"
      title="Where results fell"
      subtitle="Against the reference range recorded on each result"
    >
      <DonutChart
        title="Results by flag"
        points={points}
        centre={{ value: formatOutsideRangeShare(flags), label: 'outside range' }}
        format={formatterFor('count')}
        size={210}
      />
      <DonutLegend points={points} format={formatterFor('count')} />
      <p className="c-an__caption">
        “Not judgeable” means the result is not a measurement, or its reference range is not a
        range. It is not a kind of normal, and it is not counted as one.
      </p>
    </Card>
  );
}

export function OutsideRangeTrendCard({ series }: { series: AnalyticsSeries }) {
  return (
    <Card
      className="c-an__cell c-an__cell--wide"
      title="Outside range, over time"
      subtitle="Over judgeable results only — a gap means nothing was judgeable that period"
    >
      <LineChart
        title="Share of results outside their reference range"
        series={[toSeries(series)]}
        format={formatPercent}
        zeroBased
        height={240}
      />
    </Card>
  );
}

const UNCOVERED_COLUMNS: Column<CategoryDatum>[] = [
  { key: 'label', header: 'Analyte', render: (row) => row.label },
  {
    key: 'value',
    header: 'Results',
    align: 'end',
    render: (row) => <span className="tnum">{row.value}</span>,
  },
];

export function UncoveredCard({ uncovered }: { uncovered: CategoryDatum[] }) {
  return (
    <Card
      className="c-an__cell"
      title="Analytes no range can judge"
      subtitle="Results recorded, none of them judgeable — a reference range needs writing"
      flush
    >
      <DataTable
        caption="Analytes with no judgeable results"
        columns={UNCOVERED_COLUMNS}
        rows={uncovered}
        rowKey={(row) => row.key}
      />
    </Card>
  );
}

export function HistogramCard({ histogram }: { histogram: Histogram }) {
  return (
    <Card
      className="c-an__cell"
      title="Result distribution"
      subtitle="Patient results, not quality control — there is no control material in this system"
    >
      <BarChart
        title="Distribution of results"
        series={[toHistogramSeries(histogram)]}
        format={formatterFor('count')}
        height={220}
        categoryLabel="Value"
      />
    </Card>
  );
}

/** A right-aligned count column. */
function countColumn(
  key: 'judgeable' | 'high' | 'low' | 'not_judgeable',
  header: string,
): Column<QualityRow> {
  return {
    key,
    header,
    align: 'end',
    render: (row) => <span className="tnum">{row[key]}</span>,
  };
}

function breakdownColumns(label: string): Column<QualityRow>[] {
  return [
    { key: 'label', header: label, render: (row) => row.label },
    countColumn('judgeable', 'Judgeable'),
    countColumn('high', 'Above'),
    countColumn('low', 'Below'),
    countColumn('not_judgeable', 'Not judgeable'),
    {
      key: 'rate',
      header: 'Outside range',
      align: 'end',
      // A dash where nothing could be judged. "0%" would read as a clean run,
      // which is the opposite of what it means.
      render: (row) => (
        <span className="tnum">
          {row.abnormal_rate === null ? '—' : formatPercent(row.abnormal_rate)}
        </span>
      ),
    },
  ];
}

export function QualityBreakdownCard({
  label,
  breakdown,
}: {
  label: string;
  breakdown: QualityRow[];
}) {
  return (
    <Card className="c-an__cell c-an__cell--wide" title={`${label} — the numbers`} flush>
      <DataTable
        caption={`Abnormal rate ${label.toLowerCase()}`}
        columns={breakdownColumns(label)}
        rows={breakdown}
        rowKey={(row) => row.key}
      />
    </Card>
  );
}
